
import React, { useEffect, useState } from 'react';
import {
    Box, Button, Grid, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel,
    Chip, Tooltip, CircularProgress, Divider,
    Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { INVOICE_APICALL } from 'helper/api/api';
import { auth, toastifySuccess, toastifyError } from 'helper/Utility';

const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
];

const CURRENT_YEAR  = new Date().getFullYear();
const YEAR_OPTIONS  = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const today = () => new Date().toISOString().split('T')[0];
const emptyItem = { name: '', amount: '', note: '', date: today() };

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d)) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function HrExpenses() {
    const userRole = auth('admin')?.user_role || auth('admin')?.role;

    // List state
    const [records, setRecords]           = useState([]);
    const [loading, setLoading]           = useState(false);
    const [filterMonth, setFilterMonth]   = useState('');
    const [filterYear, setFilterYear]     = useState(CURRENT_YEAR);
    const [expandedId, setExpandedId]     = useState(null);

    // Add-month dialog
    const [monthDlg, setMonthDlg]     = useState(false);
    const [dlgMonth, setDlgMonth]     = useState(new Date().getMonth() + 1);
    const [dlgYear, setDlgYear]       = useState(CURRENT_YEAR);
    const [dlgItems, setDlgItems]     = useState([{ ...emptyItem }]);
    const [dlgSaving, setDlgSaving]   = useState(false);

    // Add/Edit item dialog
    const [itemDlg, setItemDlg]           = useState(false);
    const [itemDlgRecord, setItemDlgRecord] = useState(null);
    const [itemForm, setItemForm]         = useState({ ...emptyItem, _id: null });
    const [itemSaving, setItemSaving]     = useState(false);

    // Delete confirm
    const [deleteDlg, setDeleteDlg]           = useState(false);
    const [deleteTarget, setDeleteTarget]     = useState(null);
    const [deleteLoading, setDeleteLoading]   = useState(false);

    // Fetch
    const fetchRecords = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterMonth) params.append('month', filterMonth);
            if (filterYear)  params.append('year',  filterYear);
            const res = await INVOICE_APICALL(`hr/getexpenses?${params.toString()}`, 'GET');
            if (res?.status === 200) setRecords(res.data || []);
        } catch (e) {
            toastifyError('Failed to load expenses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRecords(); }, [filterMonth, filterYear]);

    // Month dialog helpers
    const openMonthDlg = () => {
        setDlgMonth(new Date().getMonth() + 1);
        setDlgYear(CURRENT_YEAR);
        setDlgItems([{ ...emptyItem }]);
        setMonthDlg(true);
    };

    const dlgAddRow    = () => setDlgItems(p => [...p, { ...emptyItem }]);
    const dlgRemoveRow = (i) => setDlgItems(p => p.filter((_, idx) => idx !== i));
    const dlgItemChange = (i, field, val) =>
        setDlgItems(p => p.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

    const saveMonthExpense = async () => {
        if (dlgItems.some(it => !it.name.trim() || !it.amount)) {
            toastifyError('Please fill name & amount for all items');
            return;
        }
        setDlgSaving(true);
        try {
            const res = await INVOICE_APICALL('hr/addExpense', 'POST', {
                month: dlgMonth,
                year:  dlgYear,
                expenses: dlgItems.map(it => ({
                    name:   it.name.trim(),
                    amount: Number(it.amount),
                    note:   it.note.trim(),
                    date:   it.date || today(),
                })),
            });
            if (res?.status === 200) {
                toastifySuccess('Expense saved!');
                setMonthDlg(false);
                fetchRecords();
            } else {
                toastifyError(res?.message || 'Save failed');
            }
        } catch (e) {
            toastifyError('Server error');
        } finally {
            setDlgSaving(false);
        }
    };

    // Item dialog helpers
    const openAddItem = (recordId) => {
        setItemDlgRecord(recordId);
        setItemForm({ ...emptyItem, _id: null });
        setItemDlg(true);
    };

    const openEditItem = (recordId, item) => {
        setItemDlgRecord(recordId);
        setItemForm({
            _id:    item._id,
            name:   item.name,
            amount: item.amount,
            note:   item.note || '',
            date:   item.date ? new Date(item.date).toISOString().split('T')[0] : today(),
        });
        setItemDlg(true);
    };

    const saveItem = async () => {
        if (!itemForm.name.trim() || !itemForm.amount) {
            toastifyError('Name and amount are required');
            return;
        }
        setItemSaving(true);
        try {
            const res = await INVOICE_APICALL('hr/updateExpenseItem', 'POST', {
                recordId: itemDlgRecord,
                item: {
                    ...(itemForm._id ? { _id: itemForm._id } : {}),
                    name:   itemForm.name.trim(),
                    amount: Number(itemForm.amount),
                    note:   itemForm.note.trim(),
                    date:   itemForm.date || today(),
                },
            });
            if (res?.status === 200) {
                toastifySuccess(itemForm._id ? 'Item updated!' : 'Item added!');
                setItemDlg(false);
                fetchRecords();
            } else {
                toastifyError(res?.message || 'Failed');
            }
        } catch (e) {
            toastifyError('Server error');
        } finally {
            setItemSaving(false);
        }
    };

    // Delete single helpers
    const confirmDelete = (target) => { setDeleteTarget(target); setDeleteDlg(true); };

    const doDelete = async () => {
        setDeleteLoading(true);
        try {
            let res;
            if (deleteTarget.type === 'record') {
                res = await INVOICE_APICALL('hr/deleteExpense', 'POST', { id: deleteTarget.recordId });
            } else {
                res = await INVOICE_APICALL('hr/deleteExpenseItem', 'POST', {
                    recordId: deleteTarget.recordId,
                    itemId:   deleteTarget.itemId,
                });
            }
            if (res?.status === 200) {
                toastifySuccess('Deleted!');
                setDeleteDlg(false);
                fetchRecords();
            } else {
                toastifyError(res?.message || 'Delete failed');
            }
        } catch (e) {
            toastifyError('Server error');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Summary stats
    const totalAll = records.reduce((s, r) => s + (r.totalAmount || 0), 0);
    const canEdit  = userRole === 'hr' || userRole === 'admin';

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <ReceiptLongIcon sx={{ fontSize: 32, color: '#114CC2' }} />
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="#114CC2">Office Expenses</Typography>
                        <Typography variant="body2" color="text.secondary">Monthly office expense tracker</Typography>
                    </Box>
                </Box>

                {canEdit && (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openMonthDlg}
                            sx={{ bgcolor: '#114CC2', '&:hover': { bgcolor: '#0d3a99' }, borderRadius: 2, px: 3 }}
                        >
                            Add Monthly Expenses
                        </Button>
                    </Stack>
                )}
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, borderLeft: '4px solid #114CC2' }}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>Total (filtered)</Typography>
                        <Typography variant="h4" fontWeight={700} color="#114CC2">
                            ₹{totalAll.toLocaleString('en-IN')}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, borderLeft: '4px solid #2ecc71' }}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>Months Recorded</Typography>
                        <Typography variant="h4" fontWeight={700} color="#2ecc71">{records.length}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, borderLeft: '4px solid #e67e22' }}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>Avg per Month</Typography>
                        <Typography variant="h4" fontWeight={700} color="#e67e22">
                            ₹{records.length ? Math.round(totalAll / records.length).toLocaleString('en-IN') : 0}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <CalendarMonthIcon sx={{ color: '#114CC2' }} />
                <Typography variant="body2" fontWeight={600}>Filter:</Typography>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select label="Month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                        <MenuItem value="">All Months</MenuItem>
                        {MONTHS.map((m, i) => (
                            <MenuItem key={i} value={i + 1}>{m}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Year</InputLabel>
                    <Select label="Year" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                        {YEAR_OPTIONS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </Select>
                </FormControl>

                <Button size="small" variant="outlined" onClick={() => { setFilterMonth(''); setFilterYear(CURRENT_YEAR); }}>
                    Reset
                </Button>
            </Paper>

            {/* Records list */}
            {loading ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : records.length === 0 ? (
                <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <ReceiptLongIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography color="text.secondary">No expense records found.</Typography>
                </Paper>
            ) : (
                records.map((record) => (
                    <Paper
                        key={record._id}
                        elevation={2}
                        sx={{ mb: 2.5, borderRadius: 3, overflow: 'hidden' }}
                    >
                        {/* Month header row */}
                        <Box
                            display="flex" alignItems="center" justifyContent="space-between"
                            sx={{ px: 2, py: 2, bgcolor: '#f0f4ff', cursor: 'pointer' }}
                            onClick={() => setExpandedId(expandedId === record._id ? null : record._id)}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    label={`${MONTHS[record.month - 1]} ${record.year}`}
                                    color="primary"
                                    size="small"
                                    sx={{ bgcolor: '#114CC2', fontWeight: 600 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {record.expenses?.length || 0} items
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h6" fontWeight={700} color="#114CC2">
                                    ₹{(record.totalAmount || 0).toLocaleString('en-IN')}
                                </Typography>
                                {canEdit && (
                                    <>
                                        <Tooltip title="Add item">
                                            <IconButton size="small" color="primary"
                                                onClick={e => { e.stopPropagation(); openAddItem(record._id); }}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete month">
                                            <IconButton size="small" color="error"
                                                onClick={e => { e.stopPropagation(); confirmDelete({ type: 'record', recordId: record._id }); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                                <IconButton size="small" onClick={e => { e.stopPropagation(); setExpandedId(expandedId === record._id ? null : record._id); }}>
                                    {expandedId === record._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Expanded items table */}
                        {expandedId === record._id && (
                            <Box sx={{ px: 2, pb: 2 }}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ '& th': { fontWeight: 700, color: '#555', fontSize: '0.8rem' } }}>
                                                <TableCell>#</TableCell>
                                                <TableCell>Expense Name</TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Note</TableCell>
                                                <TableCell align="right">Amount (₹)</TableCell>
                                                {canEdit && <TableCell align="center">Actions</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(record.expenses || []).map((item, idx) => (
                                                <TableRow key={item._id} hover>
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatDate(item.date)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">{item.note || '—'}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight={600} color="#114CC2">
                                                            ₹{Number(item.amount).toLocaleString('en-IN')}
                                                        </Typography>
                                                    </TableCell>
                                                    {canEdit && (
                                                        <TableCell align="center">
                                                            <Tooltip title="Edit">
                                                                <IconButton size="small" color="primary"
                                                                    onClick={() => openEditItem(record._id, item)}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton size="small" color="error"
                                                                    onClick={() => confirmDelete({ type: 'item', recordId: record._id, itemId: item._id })}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                            {/* Total row */}
                                            <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                                                <TableCell colSpan={canEdit ? 4 : 4} />
                                                <TableCell align="right">
                                                    <Typography variant="body1" fontWeight={800} color="#114CC2">
                                                        ₹{(record.totalAmount || 0).toLocaleString('en-IN')}
                                                    </Typography>
                                                </TableCell>
                                                {canEdit && <TableCell />}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Paper>
                ))
            )}

            {/* Dialog: Add Monthly Expenses */}
            <Dialog open={monthDlg} onClose={() => setMonthDlg(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#114CC2' }}>Add Monthly Expenses</DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Month</InputLabel>
                                <Select label="Month" value={dlgMonth} onChange={e => setDlgMonth(e.target.value)}>
                                    {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select label="Year" value={dlgYear} onChange={e => setDlgYear(e.target.value)}>
                                    {YEAR_OPTIONS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Typography variant="body2" fontWeight={700} mb={1} color="#114CC2">Expense Items</Typography>

                    {dlgItems.map((item, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth size="small" label="Expense Name *"
                                        placeholder="e.g. Flowers Bill"
                                        value={item.name}
                                        onChange={e => dlgItemChange(i, 'name', e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField fullWidth size="small" label="Amount (₹) *"
                                        type="number" inputProps={{ min: 0 }}
                                        value={item.amount}
                                        onChange={e => dlgItemChange(i, 'amount', e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth size="small" label="Note"
                                        value={item.note}
                                        onChange={e => dlgItemChange(i, 'note', e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField fullWidth size="small" label="Date *" type="date"
                                        value={item.date}
                                        onChange={e => dlgItemChange(i, 'date', e.target.value)}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <IconButton color="error" size="small" onClick={() => dlgRemoveRow(i)}
                                        disabled={dlgItems.length === 1}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}

                    <Button size="small" startIcon={<AddIcon />} onClick={dlgAddRow} sx={{ mt: 0.5 }}>
                        Add Row
                    </Button>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setMonthDlg(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={saveMonthExpense} disabled={dlgSaving}
                        sx={{ bgcolor: '#114CC2', '&:hover': { bgcolor: '#0d3a99' } }}>
                        {dlgSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Expenses'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Add / Edit single item */}
            <Dialog open={itemDlg} onClose={() => setItemDlg(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#114CC2' }}>
                    {itemForm._id ? 'Edit Expense Item' : 'Add Expense Item'}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Expense Name *"
                                placeholder="e.g. Newspaper Bill"
                                value={itemForm.name}
                                onChange={e => setItemForm(p => ({ ...p, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Amount (₹) *"
                                type="number" inputProps={{ min: 0 }}
                                value={itemForm.amount}
                                onChange={e => setItemForm(p => ({ ...p, amount: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Note (optional)"
                                value={itemForm.note}
                                onChange={e => setItemForm(p => ({ ...p, note: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Date *" type="date"
                                value={itemForm.date || today()}
                                onChange={e => setItemForm(p => ({ ...p, date: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setItemDlg(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={saveItem} disabled={itemSaving}
                        sx={{ bgcolor: '#114CC2', '&:hover': { bgcolor: '#0d3a99' } }}>
                        {itemSaving ? <CircularProgress size={20} color="inherit" /> : (itemForm._id ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Single delete confirm */}
            <Dialog open={deleteDlg} onClose={() => setDeleteDlg(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteTarget?.type === 'record'
                            ? "Delete this entire month's expense record? This cannot be undone."
                            : 'Delete this expense item?'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteDlg(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" color="error" onClick={doDelete} disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}